import { useEffect, useMemo } from "react";
import { useGetDashboardQuery, useGetWidgetDataQuery } from "@webcore/services/api/api";
import { FullViewSpinner } from "@webcore/components/core/FullViewSpinner/FullViewSpinner";
import { installWidgetSnapshotResponder } from "@webcore/services/export/widgetSnapshotResponder";
import CustomCodeRunner from "@webcore/components/domain/dashboard/renderers/custom/CustomCodeRunner";

interface CustomWidgetRendererProps {
  dashboardId: string;
  viewId: string;
  widgetId: string;
  dataPipelineConfigurationId: string;
  filters: Record<string, string>;
}

/**
 * Shared renderer for a custom widget's code. In the MFE model it runs inside the ops container
 * under the read-only MFE session. All requests go through the same-origin /api/v1 path so the
 * read-only cookie is sent.
 */
export default function CustomWidgetRenderer({ dashboardId, viewId, widgetId, dataPipelineConfigurationId, filters }: CustomWidgetRendererProps) {
  // Answer the parent app's PDF-export snapshot requests so this cross-origin widget can be captured.
  useEffect(() => installWidgetSnapshotResponder(), []);

  const { data: dashboard, isLoading: dashboardLoading, isError: dashboardError } = useGetDashboardQuery({ id: dashboardId });

  const widgetCode = useMemo(() => {
    if (dashboard === undefined) {
      return null;
    }

    const view = dashboard.dashboardViews.find(v => v.id === viewId);
    const widget = view?.widgets.find(w => w.id === widgetId);
    return widget?.widgetCode ?? null;
  }, [dashboard, viewId, widgetId]);

  const { data: widgetData, isLoading: dataLoading, isError: dataError } = useGetWidgetDataQuery(
    { dashboardId, viewId, widgetId, filters, dataPipelineConfigurationId }
  );

  if (dashboardLoading || dataLoading) {
    return <FullViewSpinner />;
  }

  if (dashboardError || dataError) {
    return <div className="widget-error">Failed to load widget data.</div>;
  }

  if (widgetCode === null) {
    return <div className="widget-error">No widget code found.</div>;
  }

  if (widgetData === undefined) {
    return <div className="widget-error">No data available.</div>;
  }

  return (
    <div className="w-100 vh-100 overflow-auto">
      <div data-mfe-snapshot="true">
        <CustomCodeRunner code={widgetCode} label="Widget Error" scope={{ data: widgetData }} />
      </div>
    </div>
  );
}
